import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Trash2, Copy, Loader2, Upload, Image, Video, Link, Handshake, Ticket, Pencil, Check, X, Lock, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const PIN_OPTIONS = Array.from({ length: 100 }, (_, n) => String(n).padStart(2, '0'));

export default function AdminPartnership() {
  const [partners, setPartners] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [form, setForm] = useState({ username: '', password: '', display_name: '', promo_code: '', pin1: '', pin2: '', pin3: '' });
  const [matForm, setMatForm] = useState({ title: '', type: 'poster', file_url: '', thumbnail_url: '' });
  const token = localStorage.getItem('funnely_token');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, mRes] = await Promise.all([
        fetch(`${API}/api/admin/partners`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/creative-library`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (pRes.ok) setPartners((await pRes.json()).partners || []);
      if (mRes.ok) setMaterials((await mRes.json()).materials || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createPartner = async () => {
    if (!form.username || !form.password) { toast.error('Username & password required'); return; }
    if (!form.pin1 || !form.pin2 || !form.pin3) { toast.error('PIN combo (3 numbers) required'); return; }
    const res = await fetch(`${API}/api/admin/partners`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        username: form.username, password: form.password,
        display_name: form.display_name, promo_code: form.promo_code,
        pin_combo: [form.pin1, form.pin2, form.pin3],
      }),
    });
    if (res.ok) { toast.success('Partner created'); setShowCreate(false); setForm({ username: '', password: '', display_name: '', promo_code: '', pin1: '', pin2: '', pin3: '' }); fetchData(); }
    else { const e = await res.json(); toast.error(e.detail || 'Error'); }
  };

  const deletePartner = async (id) => {
    if (!window.confirm('Delete this partner?')) return;
    const res = await fetch(`${API}/api/admin/partners/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { toast.success('Partner deleted'); fetchData(); }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    const pins = p.pin_combo || [];
    setEditForm({
      username: p.username || '',
      display_name: p.display_name || '',
      promo_code: p.promo_code || '',
      password: '',
      pin1: pins[0] || '', pin2: pins[1] || '', pin3: pins[2] || '',
    });
  };

  const saveEdit = async (partnerId) => {
    const payload = {};
    if (editForm.username) payload.username = editForm.username;
    if (editForm.display_name) payload.display_name = editForm.display_name;
    if (editForm.promo_code !== undefined) payload.promo_code = editForm.promo_code;
    if (editForm.password) payload.password = editForm.password;
    if (editForm.pin1 && editForm.pin2 && editForm.pin3) payload.pin_combo = [editForm.pin1, editForm.pin2, editForm.pin3];
    const res = await fetch(`${API}/api/admin/partners/${partnerId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    if (res.ok) { toast.success('Partner updated'); setEditingId(null); fetchData(); }
    else { const e = await res.json(); toast.error(e.detail || 'Error'); }
  };

  const addMaterial = async () => {
    if (!matForm.title || !matForm.file_url) { toast.error('Title & file URL required'); return; }
    const res = await fetch(`${API}/api/admin/creative-library`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(matForm),
    });
    if (res.ok) { toast.success('Material added'); setShowAddMaterial(false); setMatForm({ title: '', type: 'poster', file_url: '', thumbnail_url: '' }); fetchData(); }
    else { const e = await res.json(); toast.error(e.detail || 'Error'); }
  };

  const deleteMaterial = async (id) => {
    if (!window.confirm('Delete this material?')) return;
    const res = await fetch(`${API}/api/admin/creative-library/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { toast.success('Deleted'); fetchData(); }
  };

  const copyText = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied!`); };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#FF9A00]" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-partnership">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Handshake className="h-6 w-6 text-[#FF9A00]" /> Partnership Management</h1>
        <p className="text-sm text-slate-500 mt-1">Manage KOL partner accounts, promo codes, and creative materials</p>
      </div>

      <Tabs defaultValue="partners">
        <TabsList>
          <TabsTrigger value="partners">Partners ({partners.length})</TabsTrigger>
          <TabsTrigger value="library">Creative Library ({materials.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="partners" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowCreate(!showCreate)} className="bg-[#195259] hover:bg-[#0F3338]" data-testid="create-partner-btn">
              <Plus className="h-4 w-4 mr-1" /> Create Partner
            </Button>
          </div>

          {showCreate && (
            <Card data-testid="create-partner-form">
              <CardContent className="pt-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input placeholder="Username" value={form.username} onChange={e => setForm({...form, username: e.target.value})} data-testid="partner-username-input" />
                  <Input placeholder="Password" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} data-testid="partner-password-input" />
                  <Input placeholder="Display Name (optional)" value={form.display_name} onChange={e => setForm({...form, display_name: e.target.value})} data-testid="partner-displayname-input" />
                  <Input placeholder="Promo Code (e.g. FUNNELY30)" value={form.promo_code} onChange={e => setForm({...form, promo_code: e.target.value.toUpperCase()})} data-testid="partner-promocode-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1"><Lock className="h-3 w-3" /> PIN Combo (3 numbers for 2nd layer auth)</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['pin1', 'pin2', 'pin3'].map((key, i) => (
                      <Select key={key} value={form[key]} onValueChange={v => setForm({...form, [key]: v})}>
                        <SelectTrigger data-testid={`partner-pin-${i}`} className="h-10"><SelectValue placeholder={`PIN ${i+1}`} /></SelectTrigger>
                        <SelectContent className="max-h-48">
                          {PIN_OPTIONS.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={createPartner} className="bg-[#FF9A00] hover:bg-[#e08a00] text-white" data-testid="submit-partner-btn">Create</Button>
                  <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-3">
            {partners.map(p => (
              <Card key={p.id} data-testid={`partner-card-${p.username}`}>
                <CardContent className="pt-4">
                  {editingId === p.id ? (
                    /* Edit Mode */
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Pencil className="h-4 w-4 text-[#FF9A00]" />
                        <span className="text-sm font-semibold text-slate-700">Edit Partner</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500 uppercase">Username</label>
                          <Input value={editForm.username} onChange={e => setEditForm({...editForm, username: e.target.value})} className="mt-0.5" data-testid="edit-username" />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500 uppercase">New Password <span className="text-slate-400">(leave blank to keep)</span></label>
                          <Input type="password" placeholder="New password" value={editForm.password} onChange={e => setEditForm({...editForm, password: e.target.value})} className="mt-0.5" data-testid="edit-password" />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500 uppercase">Display Name</label>
                          <Input value={editForm.display_name} onChange={e => setEditForm({...editForm, display_name: e.target.value})} className="mt-0.5" data-testid="edit-displayname" />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500 uppercase">Promo Code</label>
                          <Input value={editForm.promo_code} onChange={e => setEditForm({...editForm, promo_code: e.target.value.toUpperCase()})} className="mt-0.5" data-testid="edit-promocode" />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-slate-500 uppercase flex items-center gap-1"><Lock className="h-3 w-3" /> PIN Combo</label>
                        <div className="grid grid-cols-3 gap-2 mt-0.5">
                          {['pin1', 'pin2', 'pin3'].map((key, i) => (
                            <Select key={key} value={editForm[key]} onValueChange={v => setEditForm({...editForm, [key]: v})}>
                              <SelectTrigger className="h-9" data-testid={`edit-pin-${i}`}><SelectValue placeholder="--" /></SelectTrigger>
                              <SelectContent className="max-h-48">
                                {PIN_OPTIONS.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button size="sm" onClick={() => saveEdit(p.id)} className="bg-[#FF9A00] hover:bg-[#e08a00] text-white" data-testid="save-edit-btn">
                          <Save className="h-3.5 w-3.5 mr-1" /> Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <>
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-[#195259] flex items-center justify-center text-white font-bold text-sm">
                            {p.display_name?.[0]?.toUpperCase() || 'P'}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{p.display_name}</p>
                            <p className="text-xs text-slate-500">@{p.username}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm flex-wrap">
                          <div className="text-center">
                            <p className="font-bold text-slate-900">{p.total_users || 0}</p>
                            <p className="text-xs text-slate-500">Users</p>
                          </div>
                          <div className="text-center">
                            <p className="font-bold text-[#FF9A00]">{p.paid_users || 0}</p>
                            <p className="text-xs text-slate-500">Paid</p>
                          </div>
                          <div className="text-center">
                            <p className="font-bold text-green-600">RM{(p.commission || 0).toFixed(2)}</p>
                            <p className="text-xs text-slate-500">Total Comm.</p>
                          </div>
                          <Badge variant="outline" className="text-[#195259] border-[#195259]">{p.level || 'Starter'}</Badge>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t flex items-center gap-3 flex-wrap">
                        {p.promo_code && (
                          <Button variant="ghost" size="sm" onClick={() => copyText(p.promo_code, 'Promo code')} className="text-xs h-7 px-2">
                            <Ticket className="h-3 w-3 mr-1 text-[#FF9A00]" /> Promo: <span className="font-bold text-[#FF9A00] ml-1">{p.promo_code}</span>
                          </Button>
                        )}
                        {p.pin_combo && (
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Lock className="h-3 w-3" /> PIN: {p.pin_combo.join(' - ')}
                          </span>
                        )}
                        <div className="ml-auto flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-[#FF9A00]" onClick={() => startEdit(p)} data-testid={`edit-partner-${p.username}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => deletePartner(p.id)} data-testid={`delete-partner-${p.username}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
            {partners.length === 0 && <p className="text-center text-slate-400 py-8">No partners yet. Create one above.</p>}
          </div>
        </TabsContent>

        <TabsContent value="library" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowAddMaterial(!showAddMaterial)} className="bg-[#195259] hover:bg-[#0F3338]" data-testid="add-material-btn">
              <Upload className="h-4 w-4 mr-1" /> Add Material
            </Button>
          </div>

          {showAddMaterial && (
            <Card data-testid="add-material-form">
              <CardContent className="pt-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input placeholder="Title" value={matForm.title} onChange={e => setMatForm({...matForm, title: e.target.value})} data-testid="material-title-input" />
                  <Select value={matForm.type} onValueChange={v => setMatForm({...matForm, type: v})}>
                    <SelectTrigger data-testid="material-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="poster">Poster</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Input placeholder="File URL (direct link)" value={matForm.file_url} onChange={e => setMatForm({...matForm, file_url: e.target.value})} data-testid="material-url-input" />
                <Input placeholder="Thumbnail URL (optional)" value={matForm.thumbnail_url} onChange={e => setMatForm({...matForm, thumbnail_url: e.target.value})} />
                <div className="flex gap-2">
                  <Button onClick={addMaterial} className="bg-[#FF9A00] hover:bg-[#e08a00] text-white" data-testid="submit-material-btn">Add</Button>
                  <Button variant="outline" onClick={() => setShowAddMaterial(false)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {materials.map(m => (
              <Card key={m.id} data-testid={`material-card-${m.id}`}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {m.type === 'poster' ? <Image className="h-5 w-5 text-blue-500" /> : <Video className="h-5 w-5 text-purple-500" />}
                      <div>
                        <p className="font-medium text-sm">{m.title}</p>
                        <Badge variant="outline" className="text-xs mt-1">{m.type}</Badge>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => deleteMaterial(m.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <a href={m.file_url} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline mt-2 flex items-center gap-1">
                    <Link className="h-3 w-3" /> View file
                  </a>
                </CardContent>
              </Card>
            ))}
            {materials.length === 0 && <p className="text-center text-slate-400 py-8 col-span-full">No materials. Add posters or videos above.</p>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
